import katex from 'katex'
import 'katex/contrib/mhchem/mhchem'

const IMAGE_BASE_URL = 'https://api.myneetbuddy.com/mnb-qb-images'

/**
 * Renders HTML content with embedded LaTeX expressions
 * Supports both inline ($...$) and display ($$...$$) math
 * Also supports mhchem for chemistry formulas (\ce{...})
 */
export function renderLatexContent(htmlContent: string): string {
  if (!htmlContent) return ''

  let result = htmlContent

  // Process display math ($$...$$) first
  result = result.replace(/\$\$([\s\S]*?)\$\$/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: true,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error: ${latex}]</span>`
    }
  })

  // Process inline math ($...$)
  result = result.replace(/\$([^$\n]+?)\$/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: false,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error: ${latex}]</span>`
    }
  })

  // Process \( ... \) inline math (alternative syntax)
  result = result.replace(/\\\(([^)]+?)\\\)/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: false,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error: ${latex}]</span>`
    }
  })

  // Process \[ ... \] display math (alternative syntax)
  result = result.replace(/\\\[([\s\S]*?)\\\]/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: true,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error: ${latex}]</span>`
    }
  })

  return result
}

/**
 * Converts #img[image_name] placeholders to actual img elements
 * URL pattern: https://api.myneetbuddy.com/mnb-qb-images/<test_code>/<image_name>
 */
export function processImagePlaceholders(content: string, testCode?: string): string {
  if (!content || !testCode) return content

  return content.replace(/#img\[([^\]]+)\]/g, (_, imageName) => {
    const imageUrl = `${IMAGE_BASE_URL}/${testCode}/${imageName}`
    return `<img src="${imageUrl}" alt="${imageName}" class="max-w-full h-auto my-2 rounded-lg" />`
  })
}

/**
 * Short single-line preview of question text for list rows: tags stripped,
 * images shown as [Image], display math rendered inline
 */
export function toQuestionSnippet(content: string): string {
  if (!content) return ''
  const text = content
    .replace(/<[^>]+>/g, ' ')
    .replace(/#img\[[^\]]+\]/g, '[Image]')
    .replace(/\$\$([\s\S]*?)\$\$/g, (_, latex) => `$${latex}$`)
    .replace(/\s+/g, ' ')
    .trim()
  return renderLatexContent(text)
}
